import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { FilePdf, DownloadSimple, MagnifyingGlass, Package, CaretDown, CaretUp } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const gradeLabel = (list) => {
  if (!list?.length) return null;
  return list.length === 1 ? `Grade ${list[0]}` : `Grades ${list.join(", ")}`;
};

const gradesOf = (p) => (Array.isArray(p.grades) && p.grades.length ? p.grades : (p.grade ? [p.grade] : []));

const PdfCard = ({ p, email, compact }) => {
  const url = `${API}/pdfs/${p.id}/download?email=${encodeURIComponent(email)}`;
  const grades = gradesOf(p);
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-muted/30 hover:bg-muted/50 border-2 border-foreground rounded-xl ${compact ? "p-3" : "p-4"} flex items-center gap-3 transition-colors`}
      data-testid={`member-pdf-${p.id}`}
    >
      <div className={`${compact ? "w-9 h-9" : "w-11 h-11"} bg-red-50 border-2 border-foreground rounded-lg flex items-center justify-center shrink-0`}>
        <FilePdf size={compact ? 20 : 24} weight="duotone" className="text-red-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-fredoka font-bold text-foreground truncate ${compact ? "text-sm" : ""}`}>{p.title}</p>
        <div className="flex gap-1 mt-1 flex-wrap">
          {!compact && (
            <span className="text-xs font-nunito font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              {p.topic}
            </span>
          )}
          {gradeLabel(grades) && !compact && (
            <span className="text-xs font-nunito font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
              {gradeLabel(grades)}
            </span>
          )}
          {p.resource_type && (
            <span className="text-xs font-nunito font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
              {p.resource_type}
            </span>
          )}
        </div>
      </div>
      <DownloadSimple size={compact ? 18 : 20} weight="bold" className="text-foreground shrink-0" />
    </motion.a>
  );
};

const ProductCard = ({ product, email }) => {
  const [open, setOpen] = useState(true);
  const grades = gradesOf(product);
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border-2 border-foreground rounded-2xl shadow-brutal-sm overflow-hidden"
      data-testid={`member-product-${product.id}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 hover:bg-muted/20"
      >
        <div className="w-11 h-11 bg-purple-50 border-2 border-foreground rounded-lg flex items-center justify-center shrink-0">
          <Package size={24} weight="duotone" className="text-accent-purple" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="font-fredoka font-bold text-foreground truncate">{product.title}</p>
          {product.description && (
            <p className="font-nunito text-xs text-muted-foreground line-clamp-1">{product.description}</p>
          )}
          <div className="flex gap-1 mt-1 flex-wrap">
            <span className="text-xs font-nunito font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
              {product.topic}
            </span>
            {gradeLabel(grades) && (
              <span className="text-xs font-nunito font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                {gradeLabel(grades)}
              </span>
            )}
            <span className="text-xs font-nunito font-bold bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
              {product.pdfs?.length || 0} file{(product.pdfs?.length || 0) === 1 ? "" : "s"}
            </span>
          </div>
        </div>
        {open ? <CaretUp size={20} /> : <CaretDown size={20} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 pt-0 space-y-2 bg-muted/10 border-t-2 border-foreground/10">
              {(product.pdfs || []).length === 0 ? (
                <p className="font-nunito text-sm text-muted-foreground text-center py-4">
                  No files in this bundle yet.
                </p>
              ) : (
                product.pdfs.map((p) => <PdfCard key={p.id} p={p} email={email} compact />)
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const MemberPdfLibrary = ({ email }) => {
  const [products, setProducts] = useState([]);
  const [pdfs, setPdfs] = useState([]);
  const [meta, setMeta] = useState({ topics: [], grades: [], types: [] });
  const [topic, setTopic] = useState("All");
  const [grade, setGrade] = useState("All");
  const [resourceType, setResourceType] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (topic !== "All") params.set("topic", topic);
      if (grade !== "All") params.set("grade", grade);
      if (resourceType !== "All") params.set("resource_type", resourceType);
      if (q.trim()) params.set("q", q.trim());
      const [pdfsRes, prodsRes] = await Promise.all([
        axios.post(`${API}/pdfs/list?${params.toString()}`, { email }),
        axios.post(`${API}/products/list`, { email }),
      ]);
      setPdfs(pdfsRes.data || []);
      setProducts(prodsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load PDFs");
    }
    setLoading(false);
  };

  useEffect(() => {
    axios.get(`${API}/pdfs/meta`).then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    if (email) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, topic, grade, resourceType]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  // Filter products client-side to match top-bar filters (topic/grade)
  const filteredProducts = products.filter((p) => {
    if (topic !== "All" && p.topic !== topic) return false;
    if (grade !== "All") {
      const gs = Array.isArray(p.grades) ? p.grades : [];
      if (!gs.includes(grade)) return false;
    }
    return true;
  });

  const standalonePdfs = pdfs.filter((p) => !p.product_id);

  const totalStuff = filteredProducts.length + standalonePdfs.length;

  return (
    <div className="bg-white border-2 border-foreground rounded-2xl shadow-brutal p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-fredoka font-bold text-2xl text-foreground">📚 PDF Library</h2>
          <p className="font-nunito text-sm text-muted-foreground">
            {filteredProducts.length} bundle{filteredProducts.length === 1 ? "" : "s"} • {standalonePdfs.length} standalone file{standalonePdfs.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <form onSubmit={onSearch} className="flex-1 min-w-[200px] relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search PDFs..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 font-nunito border-2 border-foreground rounded-full"
            data-testid="member-pdf-search"
          />
        </form>
        <select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="font-nunito border-2 border-foreground rounded-full px-4 py-2 bg-white"
          data-testid="member-pdf-topic-filter"
        >
          <option value="All">All</option>
          {meta.topics.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={grade}
          onChange={(e) => setGrade(e.target.value)}
          className="font-nunito border-2 border-foreground rounded-full px-4 py-2 bg-white"
          data-testid="member-pdf-grade-filter"
        >
          <option value="All">All</option>
          {meta.grades.map((g) => (
            <option key={g} value={g}>Grade {g}</option>
          ))}
        </select>
        <select
          value={resourceType}
          onChange={(e) => setResourceType(e.target.value)}
          className="font-nunito border-2 border-foreground rounded-full px-4 py-2 bg-white"
          data-testid="member-pdf-type-filter"
        >
          <option value="All">All types</option>
          {(meta.types || []).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="font-nunito text-muted-foreground text-center py-8">Loading…</p>
      ) : totalStuff === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-foreground/20 rounded-xl">
          <FilePdf size={48} weight="duotone" className="text-muted-foreground mx-auto mb-2" />
          <p className="font-nunito text-muted-foreground">No PDFs match your filters.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredProducts.length > 0 && (
            <div>
              <h3 className="font-fredoka font-bold text-foreground mb-3 flex items-center gap-2">
                <Package size={20} weight="duotone" className="text-accent-purple" />
                Product Bundles
              </h3>
              <div className="grid gap-3">
                {filteredProducts.map((p) => (
                  <ProductCard key={p.id} product={p} email={email} />
                ))}
              </div>
            </div>
          )}
          {standalonePdfs.length > 0 && (
            <div>
              <h3 className="font-fredoka font-bold text-foreground mb-3">Standalone Resources</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {standalonePdfs.map((p) => (
                  <PdfCard key={p.id} p={p} email={email} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MemberPdfLibrary;
